import { useEffect, useRef, useState } from 'react';

import { UndoOutlined } from '@ant-design/icons';
import { setSessionStorageItem } from '@revenuemarket/browser';
import { Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { formatBusinessNumber } from '@revenuemarket/utils';
import { Select, Button, Typography, Tag, Modal } from 'antd';

import { CompanyUserPermission } from '~apis/role/type';
import BusinessEntityCreateModal from '~components/BusinessEntity/BusinessEntityCreateModal';
import { usePermissionCheck } from '~components/Role/PermissionGuard';
import { BUSINESS_ENTITY_RULE_LABEL_STORAGE_KEY } from '~components/Transactions/MappingModal/constant';
import useBusinessEntityListQuery from '~hooks/queries/useBusinessEntityListQuery';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';

interface Props {
  selectedBusinessEntity: number | number[] | null;
  setSelectedBusinessEntity: (label: number | number[] | null) => void;
  width?: number | string;
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
  footer?: React.ReactNode;
  hideFooter?: boolean;
  bordered?: boolean;
  defaultOpen?: boolean;
  type?: 'alias' | 'name';
}

const BusinessEntityLabelSelect: React.FC<Props> = ({
  selectedBusinessEntity,
  setSelectedBusinessEntity,
  width,
  multiple,
  disabled,
  footer,
  placeholder,
  hideFooter = false,
  bordered = true,
  defaultOpen = false,
  type = 'alias',
}) => {
  const [modal, contextHolder] = Modal.useModal();
  const [search, setSearch] = useState('');
  const { companyId } = useDemoAwareCompanyId();
  const { hasPermission: canManageBusinessEntities } = usePermissionCheck(
    CompanyUserPermission.CAN_MANAGE_BUSINESS_ENTITIES,
  );
  const { data: businessEntityData, refetch } = useBusinessEntityListQuery({
    companyId: companyId as string,
    favoriteOnly: false,
  });

  const selectRef = useRef<React.ElementRef<typeof Select>>(null);
  useEffect(() => {
    if (defaultOpen) {
      selectRef.current?.focus();
    }
  }, [defaultOpen]);

  // Filter `option.label` match the user type `input`
  const filterOption = (input: string, option?: { label: string; value: number }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  const businessEntityLabelOptions =
    businessEntityData?.Data?.data?.map((b) => ({
      label: `${type === 'alias' ? b.alias || b.name : b.name} ${
        b.businessRegNumber ? `(${formatBusinessNumber(b.businessRegNumber)})` : ''
      }`,
      value: b.id,
      data: b,
    })) || [];

  return (
    <>
      {contextHolder}
      <Select
        ref={selectRef}
        disabled={disabled}
        options={businessEntityLabelOptions}
        style={{
          width: width || 180,
        }}
        defaultOpen={defaultOpen}
        mode={multiple ? 'multiple' : undefined}
        showSearch
        onSearch={setSearch}
        filterOption={filterOption}
        placeholder={placeholder || '거래처 분류를 선택해주세요.'}
        value={selectedBusinessEntity}
        onChange={(value) => {
          setSelectedBusinessEntity(value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Tab' && canManageBusinessEntities) {
            overlays.open(({ isOpen, onClose }) => (
              <BusinessEntityCreateModal
                open={isOpen}
                onCancel={onClose}
                name={search}
                onCreate={(id: number) => {
                  refetch();
                  modal.confirm({
                    title: '거래처 분류 규칙을 만들어주세요',
                    content:
                      '직접 생성한 거래처는 자동으로 분류되지 않습니다. 새로운 거래처를 편리하게 활용하기 위해, 거래처 분류 규칙을 꼭 만들어주세요.',
                    okText: '규칙 설정으로 이동',
                    onOk: () => {
                      setSessionStorageItem(BUSINESS_ENTITY_RULE_LABEL_STORAGE_KEY, id);
                      window.open('/clobe/labeling', '_blank');
                    },
                  });
                }}
              />
            ));
          }
        }}
        notFoundContent={
          <Flex direction="column" gap={8} align="center">
            <Typography.Text
              style={{
                color: colorTokens.textDescription,
                fontWeight: 'bold',
              }}
            >
              검색 결과가 없습니다.
            </Typography.Text>
            {canManageBusinessEntities ? (
              <Flex direction="column" gap={4} align="center">
                <Flex align="center">
                  <Tag>tab</Tag>
                  <Typography.Text
                    style={{
                      color: colorTokens.textDescription,
                      margin: 0,
                    }}
                  >
                    키를 입력하고
                  </Typography.Text>{' '}
                </Flex>
                <Typography.Text
                  style={{
                    color: colorTokens.textDescription,
                    margin: 0,
                  }}
                >
                  새로운 거래처를 만들어보세요.
                </Typography.Text>
              </Flex>
            ) : (
              <div
                style={{
                  color: colorTokens.textDescription,
                  margin: 0,
                }}
              >
                <Typography.Text style={{ color: 'inherit' }}>
                  거래처를 추가할 권한이 없어요.
                </Typography.Text>
                <br />
                <Typography.Text style={{ color: 'inherit' }}>
                  관리자에게 아래 권한을 문의해주세요.
                </Typography.Text>
                <ul style={{ paddingLeft: 18, margin: 0, listStyle: 'disc' }}>
                  <li>
                    <Typography.Text style={{ color: 'inherit' }}>거래처 관리</Typography.Text>
                  </li>
                </ul>
              </div>
            )}
          </Flex>
        }
        dropdownRender={(menu) => (
          <>
            {menu}
            {!hideFooter && (
              <Flex align="center" gap={4}>
                {footer}
                <Button
                  icon={<UndoOutlined />}
                  onClick={() => {
                    setSelectedBusinessEntity(null);
                  }}
                >
                  초기화
                </Button>
              </Flex>
            )}
          </>
        )}
        bordered={bordered}
      />
    </>
  );
};

export default BusinessEntityLabelSelect;
